import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, _setUser] = useState(() => {
        try {
            const raw = localStorage.getItem('qm_user');
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });
    const [loading, setLoading] = useState(true);

    const setUser = useCallback((u) => {
        if (u) localStorage.setItem('qm_user', JSON.stringify(u));
        else localStorage.removeItem('qm_user');
        _setUser(u);
    }, []);

    const persist = (token, u) => {
        if (token) localStorage.setItem('qm_token', token);
        setUser(u);
    };

    const login = useCallback(async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        persist(data.token, data.user);
        return data.user;
    }, []);

    const register = useCallback(async (email, password, name, resetMethod = 'recovery_code') => {
        const { data } = await api.post('/auth/register', {
            email,
            password,
            name,
            reset_method: resetMethod,
        });
        persist(data.token, data.user);
        // Caller receives recovery_code (one-time) so it can be displayed.
        return { user: data.user, recovery_code: data.recovery_code };
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('qm_token');
        setUser(null);
    }, [setUser]);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data);
        } catch {
            // ignore
        }
    }, [setUser]);

    useEffect(() => {
        const token = localStorage.getItem('qm_token');
        if (token) refresh().finally(() => setLoading(false));
        else setLoading(false);
    }, [refresh]);

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
